<template>
  <div class="configurations">
    <div class="configurations-header">
      <PageHeader title="Gerenciamento de permissões" />
      <div>
      </div>
    </div>

    <div class="collapses-container">
      <UICollapsed
          v-for="(dept, index) in departments"
          :key="`${dept.id}-${index}`"
          :header-text="dept.headerText"
          :sub-title="dept.subTitle"
          :meta-data="dept.metaData"
      >
        <template #items>
          <UIDataTable
              :columns="columns"
              :rows="dept.users"
              id-key="id"
              :show-actions="true"
              action-label="Ações"
          >
            <template #cell-status="{ row }">
              <UIBadge
                  :variant="row && row.status ? 'success' : 'danger'"
                  pill
              >
                {{ row.status ? 'Ativo' : 'Desativado' }}
              </UIBadge>
            </template>

            <template #actions="{ row }">
              <button
                  type="button"
                  class="configurations-action-button"
                  @click="openPermissionsModal(row, dept)"
                  aria-label="Visualizar permissões"
              >
                <img
                    class="configurations-action-icon"
                    src="@assets/img/icons/eye.svg"
                    alt=""
                    role="presentation"
                />
              </button>
            </template>
          </UIDataTable>
        </template>
      </UICollapsed>
    </div>

    <edit-permissions-modal
      v-if="userPermissions.user.id"
      :user="userPermissions.user"
      :permissions="userPermissions.permissions"
      :modal-id="modalEdit"
    />
  </div>
</template>

<script>
import appConfig from '@src/app.config'
import PageHeader from '@components/page-header'
import UICollapsed from '@components/ui-design/UICollapsed.vue';
import UIDataTable from '@components/ui-design/UITable.vue';
import UIBadge from '@components/ui-design/UIBadge';
import EditPermissionsModal from "@views/configurations/components/EditPermissionsModal.vue";
import PermissionsService from "@services/resources/PermissionsService";
import { RoleDescriptions, getRoles  } from "./enums/roles";

const service =  PermissionsService.build();

export default {
  name: 'ConfigurationsList',
  page: {
    title: 'Configurações',
    meta: [{ name: 'Configurações de usuários', content: appConfig.description }],
  },
  components: {
    EditPermissionsModal,
    PageHeader,
    UICollapsed,
    UIDataTable,
    UIBadge
  },
  data() {
    return {
      loading: false,
      permissions: [],
      userPermissions: {
        user: {},
        permissions: [],
      },
      groupUsers: [],
      modalEdit: "edit-user-modal",
      selectedRow: {
        user: {},
        department: {
          permissions: []
        }
      },
      currentAction: null,
      columns: [
        { key: 'id', label: 'ID' },
        { key: 'name', label: 'Nome' },
        { key: 'email', label: 'Email' },
        { key: 'date', label: 'Data de liberação' },
        { key: 'grantedBy', label: 'Concedido por' },
        { key: 'status', label: 'Status' },
      ],
      departments: []
    }
  },
 created() {
    this.fetchAllPermissions();
    this.fetchGroupUsers();
    this.shouldFetchAdmRoles();
 },
  methods: {
    fetchAllPermissions() {
      this.loading = true;
      service.getAllPermissions()
        .then(res => {
          this.permissions = res.data
        })
        .catch(err => {
          this.showToast('Erro ao processar informação', 'Erro', 'danger')
          console.error(err)
        })
        .finally(() => {
          this.loading = false
        });
    },
    shouldFetchAdmRoles() {
      const profile = this.$store.getters.getProfile || {}
      const hasNewPermissions = !!profile.use_action_permissions
      const roles = this.$store.getters.getAdmRoles || []

      if (!hasNewPermissions || (Array.isArray(roles) && roles.length > 0)) {
        return
      }

      this.$store.dispatch('fetchAdmRoles').catch(err => {
        console.error('Erro ao carregar lista de papéis administrativos', err)
      })
    },
    fetchGroupUsers() {
      this.loading = true
      return service.getGroupUsers()
          .then(res => {
            const raw = res.data
            const allowedRoles = getRoles()

            this.departments = raw
              .filter(group => allowedRoles.includes(group.role))
              .map(group => {
                const role = group.role
                return {
                  id: group.role_id || role,
                  departmentId: group.role_id || group.id || null,
                  headerText: group.label || role,
                  subTitle: RoleDescriptions[role] || '',
                  metaData: [
                    {
                      value: Array.isArray(group.users) ? group.users.length : 0,
                      label: 'Usuários'
                    }
                  ],
                  role: role,
                  users: (group.users || []).map(u => ({
                    id: u.id,
                    name: u.name,
                    email: u.email,
                    date: u.granted_at,
                    granted_at: u.granted_at,
                    grantedBy: u.granted_by,
                    role: role,
                    labelRole: group.label || role,
                    active: u.status === 1 || u.status === true,
                    status: (u.status === 1 || u.status === true) ? 1 : 0,
                    departmentId: group.role_id || group.id || null
                  })),
                  permissions: group.permissions || []
                }
              })
          })
          .catch(err => {
            this.showToast('Erro ao processar informação', 'Erro', 'danger')
            console.error(err)
          })
          .finally(() => {
            this.loading = false
          })
    },
    async getPermissionByUser(userId) {
      const rawId = String(userId != null ? userId : '').trim()
      if (!/^\d+$/.test(rawId)) {
        this.showToast('ID do usuário inválido', 'Erro', 'danger');
        return false;
      }
      this.loading = true;

      try {
        const res = await service.getUserPermissions(rawId);
        this.userPermissions = res.data;
        return true;
      } catch (err) {
        console.error(err);
        this.showToast('Erro ao buscar permissões', 'Erro', 'danger');
        this.userPermissions = {
          user: {},
          permissions: [],
        };
        return false;
      } finally {
        this.loading = false;
      }
    },
    async openPermissionsModal(row, dept) {
      await this.openModal('view-permissions', row, this.modalEdit, dept)
    },
    async openModal(action, row, modalId, dept = {}) {
      this.currentAction = action
      this.selectedRow = {
        user: row != null ? row : {},
        department: { permissions: [], ...(dept != null ? dept : {}) },
      }

      const ok = await this.getPermissionByUser(this.selectedRow.user && this.selectedRow.user.id);
      if (!ok) return;

      this.$nextTick(() => {
        this.$bvModal.show(modalId)
      })
    },
    showToast(message, title = '', variant = 'info') {
      this.$bvToast.toast(message, {
        title,
        autoHideDelay: 5000,
        appendToast: true,
        variant,
        solid: true
      })
    }
  },
}
</script>

<style lang="scss" scoped>
.configurations {
  padding: 0 24px;
  display: flex;
  flex-direction: column;
  gap: 51px;
  justify-content: center;
  flex: 1;

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 16px;
    margin-top: 1.875rem;
  }

  &-icon {
    display: flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
  }
}

.collapses-container {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.configurations ::v-deep .ui-table-header .ui-table-cell {
  cursor: default;
}

.configurations-action-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.configurations-action-icon {
  width: 20px;
  height: 20px;
}
</style>
